import { useCallback } from 'react';
import { useToast } from '../../components/ui/Toast';

/**
 * Sales Tools — Phase 3.2.
 * Thin wrapper around the /api/merchant/checkout-links endpoints. Owns
 * the toast feedback so callers don't have to repeat it. Returns the
 * shaped link row on success, or null on failure.
 */
export default function useCheckoutLinkActions() {
  const toast = useToast();

  const createLink = useCallback(
    async ({ cart, customerInfo, notes, discountCode, deliveryZone, expiresInDays }) => {
      try {
        const resp = await fetch('/api/merchant/checkout-links', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cart,
            customerInfo,
            notes,
            discountCode,
            deliveryZone,
            expiresInDays,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not create checkout link');
          return null;
        }
        return data.link;
      } catch {
        toast.error('Network error');
        return null;
      }
    },
    [toast]
  );

  const emailLink = useCallback(
    async (linkId, to) => {
      try {
        const resp = await fetch(
          `/api/merchant/checkout-links/${encodeURIComponent(linkId)}/email`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(to ? { to } : {}),
          }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not send email');
          return null;
        }
        toast.success('Checkout link emailed');
        return data.link;
      } catch {
        toast.error('Network error');
        return null;
      }
    },
    [toast]
  );

  const cancelLink = useCallback(
    async (linkId) => {
      try {
        const resp = await fetch(
          `/api/merchant/checkout-links/${encodeURIComponent(linkId)}/cancel`,
          { method: 'POST', credentials: 'include' }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          toast.error(data.error || 'Could not cancel link');
          return false;
        }
        toast.success('Link cancelled');
        return true;
      } catch {
        toast.error('Network error');
        return false;
      }
    },
    [toast]
  );

  return { createLink, emailLink, cancelLink };
}
